/* H7 Judge.me embed
   Fetches the store's real reviews from Judge.me's public reviews_for_widget
   endpoint and draws them, for templates where Judge.me's own app block cannot
   render because there is no Liquid `product` to hang it on.

   The fetch waits until the section is near the viewport. On a landing page it
   sits below an article, a buy box and the review tabs, so for most readers it
   never runs at all. */

(() => {
  const root = document.querySelector('[data-h7-jm]');
  if (!root) return;

  const list = root.querySelector('[data-h7-jm-list]');
  const summary = root.querySelector('[data-h7-jm-summary]');
  const state = root.querySelector('[data-h7-jm-state]');
  const more = root.querySelector('[data-h7-jm-more]');

  const productId = root.dataset.productId;
  const shopDomain = root.dataset.shopDomain;
  const perPage = Number(root.dataset.perPage || 8);
  if (!productId || !shopDomain) return;

  let page = 0;
  let totalPages = 1;
  let loading = false;

  const endpoint = (n) =>
    'https://judge.me/reviews/reviews_for_widget' +
    `?url=${encodeURIComponent(shopDomain)}` +
    `&shop_domain=${encodeURIComponent(shopDomain)}` +
    '&platform=shopify' +
    `&page=${n}&per_page=${perPage}` +
    `&product_id=${encodeURIComponent(productId)}`;

  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );

  // Review bodies arrive as HTML from Judge.me. They are shopper-written, so
  // they are parsed inert and stripped of anything executable before they go
  // near the document: no scripts, no frames, no event handlers, no javascript:
  // URLs. What survives is the formatting a review actually uses.
  const ALLOWED = new Set(['P', 'BR', 'EM', 'I', 'STRONG', 'B', 'U', 'UL', 'OL', 'LI', 'SPAN', 'A', 'DIV']);
  const cleanHtml = (html) => {
    const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
    const walk = (node) => {
      [...node.children].forEach((el) => {
        if (!ALLOWED.has(el.tagName)) {
          el.replaceWith(...el.childNodes);
          return;
        }
        [...el.attributes].forEach((a) => {
          const name = a.name.toLowerCase();
          const isSafeHref = name === 'href' && /^(https?:|mailto:|\/)/i.test(a.value.trim());
          if (!isSafeHref) el.removeAttribute(a.name);
        });
        if (el.tagName === 'A') {
          el.setAttribute('rel', 'nofollow noopener');
          el.setAttribute('target', '_blank');
        }
        walk(el);
      });
    };
    walk(doc.body);
    return doc.body.innerHTML;
  };

  const stars = (rating) => {
    const n = Math.round(Number(rating) || 0);
    return (
      `<span class="h7-jm__stars" role="img" aria-label="${n} out of 5 stars">` +
      '★'.repeat(n) +
      `<span class="h7-jm__stars-off">${'★'.repeat(5 - n)}</span>` +
      '</span>'
    );
  };

  const when = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const renderSummary = (data) => {
    const avg = Number(data.average_rating || 0).toFixed(1);
    const bars = (data.histogram || [])
      .map(
        (h) =>
          `<li class="h7-jm__bar-row"><span class="h7-jm__bar-label">${h.rating}★</span>` +
          `<span class="h7-jm__bar"><span style="width:${Number(h.percentage) || 0}%"></span></span>` +
          `<span class="h7-jm__bar-count">${Number(h.frequency) || 0}</span></li>`
      )
      .join('');
    summary.innerHTML =
      '<div class="h7-jm__score">' +
      `<span class="h7-jm__score-figure">${esc(avg)}</span>` +
      stars(avg) +
      `<span class="h7-jm__score-count">${esc(data.review_summary_text || `Based on ${data.number_of_reviews} reviews`)}</span>` +
      '</div>' +
      `<ul class="h7-jm__bars">${bars}</ul>`;
    summary.hidden = false;
  };

  const renderReviews = (reviews) => {
    const html = reviews
      .map((r) => {
        const pics = (r.pictures_urls || [])
          .slice(0, 4)
          .map((p) => {
            const src = typeof p === 'string' ? p : p && (p.small || p.compact || p.original || p.huge);
            if (!src || !/^https?:\/\//i.test(src)) return '';
            return `<img class="h7-jm__photo" src="${esc(src)}" alt="" loading="lazy" decoding="async" width="160" height="160">`;
          })
          .join('');
        const verified = r.verified_buyer
          ? '<span class="h7-jm__verified">Verified buyer</span>'
          : '';
        const title = r.title ? `<p class="h7-jm__review-title">${esc(r.title)}</p>` : '';
        const reply = r.reply_content
          ? `<div class="h7-jm__reply"><span class="h7-jm__reply-who">${esc(r.shop_reply_name || 'Hour Seven')}</span>${cleanHtml(r.reply_content)}</div>`
          : '';
        return (
          '<article class="h7-jm__review">' +
          '<header class="h7-jm__review-head">' +
          `<span class="h7-jm__avatar" aria-hidden="true">${esc(r.reviewer_initial || '?')}</span>` +
          '<span class="h7-jm__who">' +
          `<span class="h7-jm__name">${esc(r.reviewer_name || 'Anonymous')}</span>` +
          verified +
          '</span>' +
          `<time class="h7-jm__date" datetime="${esc(r.created_at)}">${esc(when(r.created_at))}</time>` +
          '</header>' +
          stars(r.rating) +
          title +
          `<div class="h7-jm__body">${cleanHtml(r.body_html)}</div>` +
          (pics ? `<div class="h7-jm__photos">${pics}</div>` : '') +
          reply +
          '</article>'
        );
      })
      .join('');
    list.insertAdjacentHTML('beforeend', html);
  };

  const load = async () => {
    if (loading || page >= totalPages) return;
    loading = true;
    const next = page + 1;
    more.disabled = true;
    try {
      const res = await fetch(endpoint(next), { headers: { accept: 'application/json' } });
      if (!res.ok) throw new Error(`Judge.me responded ${res.status}`);
      const data = await res.json();
      if (next === 1) {
        if (!data.reviews || !data.reviews.length) {
          state.textContent = 'No reviews yet.';
          return;
        }
        renderSummary(data);
      }
      totalPages = (data.pagination && data.pagination.total_pages) || next;
      page = next;
      renderReviews(data.reviews || []);
      state.hidden = true;
      more.hidden = page >= totalPages;
    } catch (err) {
      // Judge.me being unreachable is not a reason to leave a spinner running
      // under a buy box. The section says so and gets out of the way.
      state.textContent = 'Reviews are unavailable right now.';
      state.hidden = false;
      more.hidden = true;
    } finally {
      loading = false;
      more.disabled = false;
    }
  };

  more.addEventListener('click', load);

  if ('IntersectionObserver' in window) {
    const watcher = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        watcher.disconnect();
        load();
      },
      { rootMargin: '600px 0px' }
    );
    watcher.observe(root);
  } else {
    load();
  }
})();
